<template>
  <div class="materials-page">
    <header class="page-header">
      <div class="page-header__main">
        <h2 class="page-title">{{ $tt("素材管理") }}</h2>
        <p class="page-desc">{{ $tt("管理画布与工程使用的图片素材，列表展示缩略图，画布仍使用原图。") }}</p>
      </div>
      <div class="page-header__actions">
        <span class="stat-pill">
          <i class="el-icon-picture-outline"></i>
          {{ $tt("共") }} {{ totalMaterials }} {{ $tt("项") }}
        </span>
        <el-button type="primary" icon="el-icon-upload2" @click="showUploadModal">
          {{ $tt("上传素材") }}
        </el-button>
      </div>
    </header>

    <section class="filter-card">
      <el-form inline class="filter-form" @submit.native.prevent="search">
        <el-form-item :label="$tt('素材名称')">
          <el-input
            v-model="searchValue"
            :placeholder="$tt('请输入文件名')"
            clearable
            prefix-icon="el-icon-search"
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item :label="$tt('类型')">
          <el-select
            v-model="selectedType"
            :placeholder="$tt('全部类型')"
            clearable
            style="width: 160px"
          >
            <el-option
              v-for="item in fileTypesOptions"
              :value="item.value"
              :label="$tt(item.lable)"
              :key="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">{{ $tt("搜索") }}</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">{{ $tt("重置") }}</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section class="grid-section" v-loading="listLoading">
      <div v-if="!listLoading && materialItems.length === 0" class="empty-block">
        <i class="el-icon-folder-opened empty-block__icon"></i>
        <p>{{ $tt("暂无素材") }}</p>
        <el-button type="primary" plain size="small" @click="showUploadModal">
          {{ $tt("上传第一张") }}
        </el-button>
      </div>

      <div v-else class="material-grid">
        <article
          v-for="item in materialItems"
          :key="item.id"
          class="material-card"
        >
          <div class="material-card__preview">
            <el-image
              v-if="item.picture"
              :src="item.picture"
              fit="contain"
              class="material-card__img"
              :preview-src-list="item.previewList"
            />
            <div v-else class="material-card__placeholder">
              <i class="el-icon-picture-outline"></i>
            </div>
            <span v-if="item.fileTypeLabel" class="material-card__tag">{{ item.fileTypeLabel }}</span>
            <div class="material-card__actions">
              <el-popconfirm
                :title="$tt('确定删除该素材吗？')"
                @confirm="deleteTemplate(item.id)"
              >
                <el-button
                  slot="reference"
                  type="danger"
                  size="mini"
                  plain
                  icon="el-icon-delete"
                  circle
                  @click.stop
                />
              </el-popconfirm>
            </div>
          </div>
          <div class="material-card__footer" :title="item.text">
            <span class="material-card__name">{{ item.text }}</span>
          </div>
        </article>

        <button type="button" class="material-card material-card--add" @click="showUploadModal">
          <div class="add-inner">
            <span class="add-inner__icon"><i class="el-icon-plus"></i></span>
            <span class="add-inner__text">{{ $tt("添加素材") }}</span>
          </div>
        </button>
      </div>
    </section>

    <div class="pagination-bar" v-if="totalMaterials > pageSize">
      <Pagination
        :handleSizeChange="handleSizeChange"
        :handleCurrentPageChange="handleCurrentPageChange"
        :total="totalMaterials"
        :pageNum="pageNum"
        :pageSizes="[11, 23, 35, 47]"
      />
    </div>

    <el-dialog
      :title="$tt('上传素材')"
      :visible.sync="dialogVisible"
      width="520px"
      custom-class="material-upload-dialog"
      @close="handleCloseDialog"
    >
      <p class="upload-hint">
        {{ $tt("支持批量上传") }} · PNG / JPG / JPEG / GIF
      </p>
      <el-upload
        ref="uploadRef"
        multiple
        list-type="picture-card"
        action="/api/cloud/file/uploadFile"
        :data="uploadExtraParams"
        :headers="uploadHeaders"
        :accept="fileTypeAccepts[materailType]"
        class="material-upload"
        :file-list="file"
        :auto-upload="false"
        :on-change="fileChange"
        :on-remove="fileRemove"
        :before-upload="beforeUpload"
        :on-success="successCall"
        :on-error="errorCall"
      >
        <i class="el-icon-plus"></i>
      </el-upload>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $tt("取 消") }}</el-button>
        <el-button type="primary" :disabled="!file.length" @click="uploadImage">
          {{ $tt("开始上传") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination";
import { mapState } from "vuex";
import { materialPreviewUrl } from "@/utils/materialPreview";

export default {
  components: {
    Pagination,
  },
  data() {
    return {
      searchValue: "",
      selectedType: "",
      materialItems: [],
      totalMaterials: 0,
      listLoading: false,
      dialogVisible: false,
      materailType: "image",
      fileTypeAccepts: {
        image: ".png,.jpg,.jpeg,.gif",
        video: ".mp4",
        audio: ".mp3",
      },
      fileTypesOptions: [],
      file: [],
      fileSuccessArr: [],
      pageNum: 1,
      pageSize: 11,
    };
  },
  computed: {
    ...mapState(["loginAccountInfo"]),
    uploadExtraParams() {
      return { file_type: this.materailType };
    },
    uploadHeaders() {
      return { token: this.loginAccountInfo.token };
    },
  },
  mounted() {
    this.getMaterialsTypes();
    this.queryMaterialsList({});
  },
  methods: {
    fileTypeLabel(value) {
      const hit = this.fileTypesOptions.find((o) => o.value === value);
      return hit ? this.$tt(hit.lable) : value || "";
    },
    resetSearch() {
      this.searchValue = "";
      this.selectedType = "";
      this.pageNum = 1;
      this.queryMaterialsList({});
    },
    fileChange(file, fileList) {
      this.file = fileList;
    },
    fileRemove(file, fileList) {
      this.file = fileList;
    },
    beforeUpload() {
      this.fileSuccessArr = [];
      this.$changeLoadingState(true);
    },
    successCall(ret) {
      this.$changeLoadingState(false);
      if (ret && ret.status === "success") {
        this.fileSuccessArr.push((ret.data && ret.data.id) || "");
        if (this.fileSuccessArr.length === this.file.length) {
          this.dialogVisible = false;
          this.queryMaterialsList({});
          this.file = [];
          this.$message.success(this.$tt("上传成功"));
        }
      } else {
        this.$message.error(ret && ret.msg);
      }
    },
    handleCloseDialog() {
      this.dialogVisible = false;
      this.file = [];
    },
    errorCall(ret) {
      this.$changeLoadingState(false);
      this.$message.error(ret && ret.msg);
    },
    uploadImage() {
      if (!this.file || this.file.length === 0) {
        this.$message.error(this.$tt("请先选择要上传的图片"));
        return;
      }
      this.$refs.uploadRef.submit();
    },
    getMaterialsTypes() {
      this.$request.getTypeOptions({ type: "file_type" }).then((res) => {
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        }
        this.fileTypesOptions = res?.data || [];
      });
    },
    search() {
      this.pageNum = 1;
      this.queryMaterialsList({
        file_type: this.selectedType,
        file_name: this.searchValue,
      });
    },
    deleteTemplate(id) {
      this.$request.deleteMaterial({ id }).then((res) => {
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        }
        this.$message.success(this.$tt("删除成功"));
        this.queryMaterialsList({});
      });
    },
    showUploadModal() {
      this.file = [];
      this.fileSuccessArr = [];
      this.dialogVisible = true;
    },
    queryMaterialsList({ page_size, page_num, file_name, file_type }) {
      this.listLoading = true;
      this.$request
        .queryMaterialsList({
          page_size: page_size || this.pageSize,
          page_num: page_num || this.pageNum,
          file_name,
          file_type,
        })
        .then((res) => {
          if (res.status != "success") {
            this.$message.error(res.msg);
            return;
          }
          const rows = res?.data?.result || [];
          this.totalMaterials = res?.data?.total || 0;
          this.materialItems = rows.map((item) => {
            const original = item.file_down_path || item.fileDownPath || "";
            const thumb = materialPreviewUrl(item);
            return {
              id: item.id,
              picture: thumb || original,
              previewList: original ? [original] : thumb ? [thumb] : [],
              text: item.file_real_name || item.file_name || "—",
              fileType: item.file_type,
              fileTypeLabel: this.fileTypeLabel(item.file_type),
            };
          });
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    handleCurrentPageChange(v) {
      this.pageNum = v;
      this.queryMaterialsList({ page_num: v });
    },
    handleSizeChange(v) {
      this.pageNum = 1;
      this.pageSize = v;
      this.queryMaterialsList({ page_size: v });
    },
  },
};
</script>

<style lang="scss" scoped>
.materials-page {
  min-height: 100%;
  padding: 20px 24px 32px;
  background: linear-gradient(180deg, #f5f8fc 0%, #eef2f7 48%, #f7f9fb 100%);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  &__main {
    flex: 1;
    min-width: 240px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
}

.page-title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: #1f2d3d;
  letter-spacing: 0.02em;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: #8492a6;
  line-height: 1.5;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #5e6d82;
  background: #fff;
  border: 1px solid #e4e9f0;
  border-radius: 999px;

  i {
    color: #409eff;
  }
}

.filter-card {
  padding: 16px 20px 4px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(31, 45, 61, 0.04);
}

.filter-form {
  ::v-deep .el-form-item {
    margin-bottom: 12px;
  }

  ::v-deep .el-form-item__label {
    color: #5e6d82;
    font-weight: 500;
  }

  ::v-deep .el-input__inner {
    border-radius: 8px;
  }
}

.grid-section {
  min-height: 200px;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 16px;
}

.material-card {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  overflow: hidden;
  text-align: left;
  cursor: default;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover {
    border-color: #c6ddf5;
    box-shadow: 0 8px 24px rgba(64, 158, 255, 0.12);
    transform: translateY(-2px);

    .material-card__actions {
      opacity: 1;
    }
  }

  &__preview {
    position: relative;
    height: 148px;
    background: linear-gradient(145deg, #f8fafc, #eef2f6);
  }

  &__img {
    width: 100%;
    height: 148px;
    display: block;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 36px;
    color: #c0c4cc;
  }

  &__tag {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    max-width: calc(100% - 16px);
    padding: 2px 8px;
    font-size: 11px;
    color: #409eff;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__actions {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &__footer {
    padding: 10px 12px 12px;
    border-top: 1px solid #f0f3f7;
  }

  &__name {
    display: block;
    font-size: 13px;
    color: #3c4858;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &--add {
    min-height: 196px;
    cursor: pointer;
    border-style: dashed;
    border-color: #c5d4e8;
    background: #fafcff;
    box-shadow: none;

    &:hover {
      border-color: #409eff;
      background: #f0f7ff;
      transform: translateY(-2px);
    }

    .add-inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 196px;
      gap: 10px;
      color: #409eff;
    }

    .add-inner__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      font-size: 22px;
      background: #fff;
      border: 1px solid #d9ecff;
      border-radius: 12px;
    }

    .add-inner__text {
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.empty-block {
  padding: 64px 24px;
  text-align: center;
  background: #fff;
  border: 1px dashed #dce4ee;
  border-radius: 12px;

  &__icon {
    font-size: 48px;
    color: #c0c4cc;
    margin-bottom: 12px;
  }

  p {
    margin: 0 0 16px;
    color: #909399;
    font-size: 14px;
  }
}

.pagination-bar {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}
</style>

<style lang="scss">
.material-upload-dialog {
  border-radius: 12px;

  .el-dialog__header {
    padding: 18px 20px 12px;
    border-bottom: 1px solid #f0f3f7;
  }

  .el-dialog__body {
    padding: 16px 20px 8px !important;
  }

  .upload-hint {
    margin: 0 0 12px;
    font-size: 13px;
    color: #909399;
  }

  .material-upload {
    .el-upload-dragger {
      width: 100%;
      border-radius: 10px;
    }

    .el-upload--picture-card {
      width: 100px;
      height: 100px;
      line-height: 100px;
      border-radius: 10px;
    }

    .el-upload-list--picture-card .el-upload-list__item {
      width: 100px;
      height: 100px;
      border-radius: 10px;
    }
  }
}
</style>
